import { BarChart, Badge, Footnote, MetricLine, Modal, ProgressBar, type BarSeries } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime, formatRatio } from '../../../lib/format';
import { porUnidade } from '../comparar';
import type { EmailCampaign } from '../types';
import { EMAIL_STATUS_LABEL, EMAIL_STATUS_TONE, SEGMENT_LABEL } from '../vocabulario';

/**
 * Relatorio de uma campanha de e-mail: as MetricLines, o retorno (pagaram,
 * recuperado, comissao, custo por recuperacao) e as barras por dia.
 *
 * Decisoes nao obvias:
 *  1. As duas series das barras usam `series-1` e `series-2` NA ORDEM (regra
 *     do guia); a legenda vem do proprio BarChart.
 *  2. `day` e YYYY-MM-DD e vira "dd/mm" montando a data como hora LOCAL --
 *     `new Date('2026-09-24')` seria UTC e apareceria como o dia anterior.
 *  3. "Pagaram" e o ADR-001 (e-mail como ultimo contato, 7 dias); o custo e
 *     estimado pela tarifa por mil do provedor, so sobre os aceitos.
 */

const ROTULO_DIA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

function rotuloDoDia(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return ROTULO_DIA.format(new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

export function ModalRelatorioEmail({ campanha, porMilCents, onClose }: { readonly campanha: EmailCampaign | null; readonly porMilCents: number; readonly onClose: () => void }): JSX.Element | null {
  if (campanha === null) {
    return null;
  }
  const series: readonly BarSeries[] = [
    { key: 'abertos', label: 'Abertos', values: campanha.daily.map((p) => p.opened), tone: 1 },
    { key: 'cliques', label: 'Cliques', values: campanha.daily.map((p) => p.clicked), tone: 2 },
  ];
  const custo = Math.round((campanha.acceptedCount * porMilCents) / 1_000);
  const unitario = porUnidade(custo, campanha.paidCount);

  return (
    <Modal open onClose={onClose} size="lg" title={`Relatório · ${campanha.subject}`} description={`Criada em ${formatDateTime(campanha.createdAt)} · público: ${SEGMENT_LABEL[campanha.segmentKey]}`}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={EMAIL_STATUS_TONE[campanha.status]} dot>
            {EMAIL_STATUS_LABEL[campanha.status]}
          </Badge>
          <span className="text-xs text-ink-subtle">{campanha.preheader}</span>
        </div>
        <div>
          <ProgressBar value={campanha.acceptedCount} max={campanha.totalRecipients} tone={EMAIL_STATUS_TONE[campanha.status]} label={`Progresso de ${campanha.subject}`} />
          <p className="mt-1 text-xs tabular-nums text-ink-subtle">
            {formatCount(campanha.acceptedCount)} de {formatCount(campanha.totalRecipients)} destinatários aceitos pelo provedor
          </p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <MetricLine icon="disparo" value={formatCount(campanha.acceptedCount)} label="aceitos" detail={formatRatio(campanha.acceptedCount, campanha.totalRecipients)} tone="brand" muted={campanha.acceptedCount === 0} />
          <MetricLine icon="alerta" value={formatCount(campanha.failedCount)} label="recusados" detail={formatRatio(campanha.failedCount, campanha.totalRecipients)} tone="critical" muted={campanha.failedCount === 0} />
          <MetricLine icon="olho" value={formatCount(campanha.openedCount)} label="abriram" detail={formatRatio(campanha.openedCount, campanha.acceptedCount)} tone="info" muted={campanha.openedCount === 0} />
          <MetricLine icon="clique" value={formatCount(campanha.clickedCount)} label="clicaram no botão" detail={formatRatio(campanha.clickedCount, campanha.openedCount)} tone="accent" muted={campanha.clickedCount === 0} />
          <MetricLine icon="check" value={formatCount(campanha.paidCount)} label="pagaram em até 7 dias" detail={`${formatRatio(campanha.paidCount, campanha.acceptedCount)} dos aceitos`} tone="good" muted={campanha.paidCount === 0} />
          <MetricLine icon="saida" value={formatCount(campanha.unsubscribedCount)} label="se descadastraram" detail={formatRatio(campanha.unsubscribedCount, campanha.acceptedCount)} tone="warning" muted={campanha.unsubscribedCount === 0} />
        </div>
        <div className="grid gap-3 rounded-xl border border-line bg-elevated p-4 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Recuperado</p>
            <p className="text-2xl font-semibold tracking-tight text-ink">{formatBRLFromCents(campanha.recoveredAmountCents)}</p>
            <p className="text-xs text-ink-subtle">comissão de {formatBRLFromCents(campanha.commissionCents)} para o Converte Fácil</p>
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-ink-subtle">Custo estimado</dt>
            <dd className="text-right tabular-nums text-ink">{formatBRLFromCents(custo)}</dd>
            <dt className="text-ink-subtle">Custo por recuperação</dt>
            <dd className="text-right tabular-nums text-ink">{unitario === null ? '—' : formatBRLFromCents(unitario)}</dd>
            <dt className="text-ink-subtle">Tarifa</dt>
            <dd className="text-right tabular-nums text-ink">{formatBRLFromCents(porMilCents)}/mil</dd>
          </dl>
        </div>
        {campanha.daily.length === 0 ? (
          <p className="text-sm text-ink-muted">A série por dia aparece depois que os primeiros e-mails saírem.</p>
        ) : (
          <div>
            <p className="mb-2 text-sm font-medium text-ink">Abertos e cliques por dia</p>
            <BarChart labels={campanha.daily.map((p) => rotuloDoDia(p.day))} series={series} formatValue={formatCount} title={`Abertos e cliques por dia de ${campanha.subject}`} height={180} />
          </div>
        )}
        <Footnote>Abertura depende do cliente de e-mail carregar imagens; o clique e o pagamento são as medidas mais confiáveis.</Footnote>
      </div>
    </Modal>
  );
}
